import { Layer, project32 } from "@deck.gl/core"
import type { LayerContext, LayerProps } from "@deck.gl/core"
import { Geometry, Model } from "@luma.gl/engine"
import { ColorStop, buildColormapGlsl } from "../shared/colormap"
import { FrameImage, FrameTextureCache } from "../shared/frameTextureCache"
import { HEATMAP_QUAD_POSITIONS, HEATMAP_QUAD_VERTEX_COUNT } from "../shared/heatmapGeometry"

export const LIGHTNING_RISK_COLOR_STOPS: ReadonlyArray<ColorStop> = [
    { value: 0.025, color: [1.0, 0.95, 0.6] },
    { value: 0.05, color: [1.0, 0.85, 0.3] },
    { value: 0.15, color: [1.0, 0.55, 0.2] },
    { value: 0.35, color: [0.95, 0.25, 0.2] },
    { value: 0.6, color: [0.7, 0.15, 0.55] },
    { value: 1.0, color: [0.4, 0.1, 0.6] },
]

export const LIGHTNING_MIN_VISIBLE = 0.025
export const LIGHTNING_FADE_IN_MAX = 0.05
export const LIGHTNING_RISK_MAX =
    LIGHTNING_RISK_COLOR_STOPS[LIGHTNING_RISK_COLOR_STOPS.length - 1].value

const HEATMAP_VS = `#version 300 es
in vec2 positions;
out vec2 v_normPos;

uniform vec4 u_bounds;

void main() {
    v_normPos = positions * 0.5 + 0.5;
    float lon = mix(u_bounds.x, u_bounds.z, v_normPos.x);
    float lat = mix(u_bounds.y, u_bounds.w, v_normPos.y);
    gl_Position = project_position_to_clipspace(vec3(lon, lat, 0.0), vec3(0.0), vec3(0.0));
}
`

// Risk rides in R, encoded [0,1] → [0,255], so a normalized read decodes to intensity directly.
// 8-bit unorm is hardware-linear-filterable, so bilinear replaces the numeric path's in-shader bicubic.
const HEATMAP_FS = `#version 300 es
precision highp float;
precision highp sampler2D;

in vec2 v_normPos;

uniform sampler2D u_lightningTextureA;
uniform sampler2D u_lightningTextureB;
uniform float u_imageWeight;
uniform float u_minVisible;
uniform float u_fadeInMax;
uniform float u_opacity;

out vec4 fragColor;

${buildColormapGlsl(LIGHTNING_RISK_COLOR_STOPS, "lightningColormap")}

vec4 sampleLinear(sampler2D tex, vec2 normPos) {
    return texture(tex, vec2(normPos.x, 1.0 - normPos.y));
}

void main() {
    vec4 a = sampleLinear(u_lightningTextureA, v_normPos);
    vec4 b = sampleLinear(u_lightningTextureB, v_normPos);
    float intensity = mix(a.r, b.r, u_imageWeight);
    float coverage = mix(a.a, b.a, u_imageWeight);

    float alphaScale = smoothstep(u_minVisible, u_fadeInMax, intensity) * coverage;
    fragColor = vec4(lightningColormap(intensity), u_opacity * alphaScale);
}
`

export type LightningRiskHeatmapLayerProps = LayerProps & {
    imageA: FrameImage
    imageB: FrameImage
    imageWeight: number
    bounds: [number, number, number, number]
    minVisible?: number
    fadeInMax?: number
    opacity?: number
}

const DEFAULT_MIN_VISIBLE = LIGHTNING_MIN_VISIBLE
const DEFAULT_FADE_IN_MAX = LIGHTNING_FADE_IN_MAX
const DEFAULT_OPACITY = 0.6

export class LightningRiskHeatmapLayer extends Layer<LightningRiskHeatmapLayerProps> {
    static layerName = "LightningRiskHeatmapLayer"
    static defaultProps = {
        imageWeight: { type: "number", value: 0, min: 0, max: 1 },
        bounds: { type: "array", value: [-180, -90, 180, 90], compare: true },
        minVisible: { type: "number", value: DEFAULT_MIN_VISIBLE, min: 0 },
        fadeInMax: { type: "number", value: DEFAULT_FADE_IN_MAX, min: 0 },
        opacity: { type: "number", value: DEFAULT_OPACITY, min: 0, max: 1 },
        parameters: { depthTest: false },
    }

    declare state: {
        textureCache: FrameTextureCache
        model: Model | null
    }

    getModels() {
        return this.state?.model ? [this.state.model] : []
    }

    initializeState(context: LayerContext) {
        const { device } = context

        const shaders = this.getShaders({
            vs: HEATMAP_VS,
            fs: HEATMAP_FS,
            modules: [project32],
        })
        const model = new Model(device, {
            id: "lightning-risk-heatmap",
            ...shaders,
            geometry: new Geometry({
                topology: "triangle-strip",
                attributes: {
                    positions: { size: 2, value: HEATMAP_QUAD_POSITIONS },
                },
            }),
            vertexCount: HEATMAP_QUAD_VERTEX_COUNT,
            parameters: {
                blendColorSrcFactor: "src-alpha",
                blendColorDstFactor: "one-minus-src-alpha",
                blendAlphaSrcFactor: "one",
                blendAlphaDstFactor: "one-minus-src-alpha",
            },
        })

        this.setState({ textureCache: new FrameTextureCache(), model })
    }

    finalizeState(context: LayerContext) {
        this.state.textureCache?.destroy()
        this.state.model?.destroy()
        super.finalizeState(context)
    }

    draw() {
        const { model, textureCache } = this.state
        const { imageA, imageB } = this.props
        if (!model || !imageA || imageA.width === 0 || imageA.height === 0) return

        const create = (image: FrameImage) =>
            this.context.device.createTexture({
                width: image.width,
                height: image.height,
                format: "rgba8unorm",
                data: image.bitmap,
                sampler: { magFilter: "linear", minFilter: "linear" },
            })

        model.setBindings({
            u_lightningTextureA: textureCache.get(imageA, create),
            u_lightningTextureB: textureCache.get(imageB, create),
        })
        model.setUniforms({
            u_bounds: this.props.bounds,
            u_imageWeight: this.props.imageWeight ?? 0,
            u_minVisible: this.props.minVisible ?? DEFAULT_MIN_VISIBLE,
            u_fadeInMax: this.props.fadeInMax ?? DEFAULT_FADE_IN_MAX,
            u_opacity: this.props.opacity ?? DEFAULT_OPACITY,
        })
        model.draw(this.context.renderPass)
    }
}
